import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/api/axios';
import { ThreeDots } from 'react-loader-spinner';
import LoadingOverlay from './LoadingOverlay';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/ConfirmationModal';
import {
    Card,
    Typography,
    Tabs,
    TabsHeader,
    CardBody,
} from "@material-tailwind/react";
import CustomInput from '../../components/CustomInput';
import { encryptForTransit, decryptFromTransit } from '../../utils/common';
import CustomSelect from '../../components/CustomSelect';
import { DeleteButton, EditButton, OutlineButton, PrimaryButton } from '../../components/Buttons';
import { Eye, EyeOff, Building2, Mail, Phone, BadgeCheck, ArrowLeft } from 'lucide-react';
import InstallmentsTable from './InstallmentsTable'; // Import the InstallmentsTable component

const STATIC_SUBSCRIPTION_TYPES = [
    { subscriptionTypeId: 1, typeName: 'BuildVizer Unified' },
    { subscriptionTypeId: 2, typeName: 'BuildVizer Plus' },
];
const STATIC_PAYMENT_TYPES = ['MONTHLY', 'QUARTERLY', 'YEARLY', 'UPFRONT'];

const ViewCustomer = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [installments, setInstallments] = useState([]);
    const [isLoadingOverlayVisible, setIsLoadingOverlayVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [newInstallment, setNewInstallment] = useState(null);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [installmentToDelete, setInstallmentToDelete] = useState(null);
    const [editingCustomer, setEditingCustomer] = useState(false);
    const [editingSubscription, setEditingSubscription] = useState(false);
    const [editedCustomerData, setEditedCustomerData] = useState({});
    const [editedSubscriptionData, setEditedSubscriptionData] = useState({});
    const [emailPasswordVisible, setEmailPasswordVisible] = useState(false);
    const [whatsappTokenVisible, setWhatsappTokenVisible] = useState(false);
    const [paymentTypes, setPaymentTypes] = useState(['MONTHLY', 'QUARTERLY', 'YEARLY', 'UPFRONT']);
    const [letterheadImage, setLetterheadImage] = useState('');
    const [footerImage, setFooterImage] = useState('');
    const [stampImage, setStampImage] = useState('');
    const [activeTab, setActiveTab] = useState('customerSubscription'); // State for active tab
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [previewInstallments, setPreviewInstallments] = useState([]); // Installments for preview in Modal

    const TABLE_HEAD = ["Projected Amount", "Received Amount", "Projected Date", "Received Date", "Actions"];

    const subscriptionTypeOptions = STATIC_SUBSCRIPTION_TYPES.map(type => ({
        value: type.subscriptionTypeId,
        label: type.typeName,
    }));

    const paymentTypeOptions = paymentTypes.map(type => ({
        value: type,
        label: type,
    }));

    const isInitialRender = useRef(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setIsLoadingOverlayVisible(true);
            try {
                // Fetch customer - handle both paginated and non-paginated responses
                const customerResponse = await axiosInstance.get(`/customers?customerId=${customerId}`);
                
                // Handle paginated response format
                let customerData;
                if (customerResponse?.items && customerResponse.total !== undefined) {
                    // Paginated response - get first item
                    customerData = customerResponse.items[0] || customerResponse.items;
                } else if (Array.isArray(customerResponse)) {
                    // Array response - get first item
                    customerData = customerResponse[0];
                } else {
                    // Single object response
                    customerData = customerResponse;
                }
                
                setCustomer(customerData);
                setEditedCustomerData(customerData || {});
                setEditedSubscriptionData({ customerId: customerId }); // Initialize customerId
             
                let subscriptionData = null;
                try {
                    // Fetch subscription - handle both paginated and non-paginated responses
                    const subscriptionResponse = await axiosInstance.get(`/subscriptions?customerId=${customerId}`);
                    
                    // Handle paginated response format
                    if (subscriptionResponse?.items && subscriptionResponse.total !== undefined) {
                        // Paginated response - get first item
                        subscriptionData = subscriptionResponse.items[0] || subscriptionResponse.items;
                    } else if (Array.isArray(subscriptionResponse)) {
                        // Array response - get first item
                        subscriptionData = subscriptionResponse[0];
                    } else {
                        // Single object response
                        subscriptionData = subscriptionResponse;
                    }
                    
                    setSubscription(subscriptionData);

                    if (subscriptionData) {
                        setEditedSubscriptionData({
                            ...subscriptionData,
                            customerId: customerId, // Keep the customerId
                            subscriptionTypeId: Number(subscriptionData.subscriptionTypeId)
                        });
                    } else {
                        setEditedSubscriptionData({ customerId: customerId });
                    }
                } catch (subError) {
                    if (subError.response && subError.response.status === 404) {
                        console.warn("No subscription found for this customer.");
                        setSubscription(null);
                        setInstallments([]);
                    } else {
                        throw subError;
                    }
                }

                if (subscriptionData?.subscriptionId) {
                    // Fetch installments - handle both paginated and non-paginated responses
                    const installmentResponse = await axiosInstance.get(
                        `/subscriptions/customer/installment?subscriptionId=${subscriptionData.subscriptionId}`
                    );
                    
                    // Handle paginated response format
                    const installmentsArray = installmentResponse?.items || (Array.isArray(installmentResponse) ? installmentResponse : []);
                    setInstallments(installmentsArray);
                } else {
                    setInstallments([]);
                }

            } catch (err) {
                setError(err.message);
                console.error("Error fetching customer details:", err);

            } finally {
                setIsLoading(false);
                setIsLoadingOverlayVisible(false);
                isInitialRender.current = false;
            }
        };
        fetchData();
    }, [customerId]);

    // const fetchImages = async () => {
    //     try {
    //         const response = await axiosInstance.get(`/customers/letter-head-stamp?customer_id=${customerId}`);
    //         const letterHead = response.find(item => /^header\.(png|jpg|jpeg|webp)$/i.test(item.key));
    //         const footer = response.find(item => /^footer\.(png|jpg|jpeg|webp)$/i.test(item.key));

    //         const stamp = response.find(item =>
    //             /^stamp_image\.(webp|jpeg|jpg|png)$/i.test(item.key)
    //         );

    //         console.log(stamp);
    //         setLetterheadImage(letterHead ? `data:${letterHead.contentType};base64,${letterHead.content}` : '');
    //         setFooterImage(footer ? `data:${footer.contentType};base64,${footer.content}` : '');
    //         setStampImage(stamp ? `data:${stamp.contentType};base64,${stamp.content}` : '');

    //     } catch (error) {
    //         console.error("Error fetching letterhead objects:", error);
    //         toast.error("Failed to load image data.", { position: "top-right" });
    //         setLetterheadImage('');
    //         setFooterImage('');
    //         setStampImage('');
    //     }
    // };

    const handleEditCustomer = () => {
        setEditedCustomerData({
            ...customer,
            letterheadImage: letterheadImage || null,
            footerImage: footerImage || null,
            stampImage: stampImage || null
        });
        setEditingCustomer(true);
    };

    const handleCustomerInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const addressFieldName = name.substring(8);
            setEditedCustomerData(prev => ({
                ...prev,
                address: {
                    ...(prev.address || {}),
                    [addressFieldName]: value
                }
            }));
        } else {
            setEditedCustomerData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleLetterheadImageChange = (e) => {
        const file = e.target.files[0];
        setLetterheadImage(file);
        setEditedCustomerData(prev => ({ ...prev, letterheadImage: file }));
    };

    const handleFooterImageChange = (e) => {
        const file = e.target.files[0];
        setFooterImage(file);
        setEditedCustomerData(prev => ({ ...prev, footerImage: file }));
    };

    const handleStampImageChange = (e) => {
        const file = e.target.files[0];
        setStampImage(file);
        setEditedCustomerData(prev => ({ ...prev, stampImage: file }));
    };

    const handleSaveCustomer = async () => {
        setIsLoadingOverlayVisible(true);
        try {
            const formData = new FormData();
            formData.append('company_name', editedCustomerData.companyName || '');
            formData.append('owner_name', editedCustomerData.ownerName || '');
            formData.append('mobile_no_wa', editedCustomerData.mobileNoWa || '');
            formData.append('email_id', editedCustomerData.emailId || '');
            formData.append('email_password_secret', encryptForTransit(editedCustomerData.emailPasswordSecret) || '');
            formData.append('customer_type', editedCustomerData.customerType || '');
            formData.append('rera_number', editedCustomerData.reraNumber || '');
            formData.append('is_active', editedCustomerData.isActive || false);
            formData.append('id_developer_approval_required', editedCustomerData.idDeveloperApprovalRequired ?? false);
            formData.append('meta_id', editedCustomerData.metaId || '');
            formData.append('whatsapp_secret_access_token', encryptForTransit(editedCustomerData.whatsappSecretAccessToken) || '');
            formData.append('building_no', editedCustomerData.address?.buildingNo || '');
            formData.append('street_name1', editedCustomerData.address?.streetName1 || '');
            formData.append('street_name2', editedCustomerData.address?.streetName2 || '');
            formData.append('city', editedCustomerData.address?.city || '');
            formData.append('district', editedCustomerData.address?.district || '');
            formData.append('pin_code', editedCustomerData.address?.pinCode || '');
            formData.append('state', editedCustomerData.address?.state || '');
            formData.append('country', editedCustomerData.address?.country || '');

            if (letterheadImage instanceof File) {
                formData.append('letterhead_image', letterheadImage);
            }
            if (footerImage instanceof File) {
                formData.append('footer_image', footerImage);
            }
            if (stampImage instanceof File) {
                formData.append('stamp_image', stampImage);
            }

            await axiosInstance.put(`/customers?customerId=${customerId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            // Fetch updated customer - handle both paginated and non-paginated responses
            const customerResponse = await axiosInstance.get(`/customers?customerId=${customerId}`);
            
            // Handle paginated response format
            let customerData;
            if (customerResponse?.items && customerResponse.total !== undefined) {
                customerData = customerResponse.items[0] || customerResponse.items;
            } else if (Array.isArray(customerResponse)) {
                customerData = customerResponse[0];
            } else {
                customerData = customerResponse;
            }
            
            setCustomer(customerData);
            toast.success("Customer details updated successfully!", { position: "top-right" });
        } catch (err) {
            setError(err.message);
            console.error("Error updating customer details:", err);
            toast.error(`Error updating customer: ${err.response?.data?.message || err.message}`, { position: "top-right" });
        } finally {
            setIsLoadingOverlayVisible(false);
            setEditingCustomer(false);
        }
    };

    const handleCancelCustomerEdit = () => {
        setEditedCustomerData(customer || {});
        setEditingCustomer(false);
    };

    const handleEditSubscription = () => {
        setSubscriptionModalOpen(true);
        setEditedSubscriptionData({
            ...subscription,
            customerId: customerId, // Always include customerId
            subscriptionTypeId: subscription?.subscriptionTypeId ?
                Number(subscription.subscriptionTypeId) :
                (subscription?.subscriptionType?.subscriptionTypeId ?
                    Number(subscription.subscriptionType.subscriptionTypeId) : null),
            startDate: subscription?.startDate || new Date().toISOString().slice(0, 10), // Default to today's date
            endDate: subscription?.endDate || getFutureDate(new Date(), 6).toISOString().slice(0, 10)
        });

        setPreviewInstallments([]); // Clear any existing preview installments
    };

    const getFutureDate = (date, months) => {
        const futureDate = new Date(date);
        futureDate.setMonth(date.getMonth() + months);
        return futureDate;
    };

    const generateInstallments = async (subscriptionTypeId, price = editedSubscriptionData.price, startDate = editedSubscriptionData.startDate, endDate = editedSubscriptionData.endDate, paymentType = editedSubscriptionData.paymentType) => {
        setIsLoadingOverlayVisible(true);
        try {
            const subscriptionType = STATIC_SUBSCRIPTION_TYPES.find(t => t.subscriptionTypeId === subscriptionTypeId);

            if (!subscriptionType) {
                console.warn("Invalid Subscription Type. Cannot generate installments.");
                setIsLoadingOverlayVisible(false);
                return;
            }

            const startDateObj = new Date(startDate);
            const endDateObj = new Date(endDate);

            if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
                console.warn("Invalid Start or End Date.");
                setIsLoadingOverlayVisible(false);
                return;
            }

            const totalMonths = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 +
                (endDateObj.getMonth() - startDateObj.getMonth());
            if (totalMonths <= 0) {
                console.warn("Start Date cannot be greater than End Date");
                setIsLoadingOverlayVisible(false);
                return;

            }

            let monthsBetweenPayments = 1; // Default monthly
            let frequency = "MONTHLY";

            switch (paymentType) {
                case "QUARTERLY":
                    monthsBetweenPayments = 3;
                    frequency = "QUARTERLY";
                    break;
                case "YEARLY":
                    monthsBetweenPayments = 12;
                    frequency = "YEARLY";
                    break;
                default:
                    monthsBetweenPayments = 1;
                    frequency = "MONTHLY";
                    break;
            }

            const numInstallments = Math.ceil(totalMonths / monthsBetweenPayments);
            const newInstallments = [];

            // Upfront Payment
            const upfrontAmount = price * 0.10;
            const upfrontDate = new Date(startDateObj); // Use startDate as UpfrontDate
            upfrontDate.setDate(upfrontDate.getDate() + 10);

            newInstallments.push({
                projectedAmount: upfrontAmount,
                receivedAmount: null,
                projectedDate: upfrontDate.toISOString().split('T')[0],
                receivedDate: null,
                type: "UPFRONT"
            });

            // Remaining Installments
            const remainingAmount = price - upfrontAmount;
            const installmentAmount = remainingAmount / numInstallments;

            for (let i = 1; i <= numInstallments; i++) {
                const projectedDate = new Date(startDateObj);
                projectedDate.setMonth(startDateObj.getMonth() + (i * monthsBetweenPayments));
                projectedDate.setDate(new Date(projectedDate.getFullYear(), projectedDate.getMonth() + 1, 0).getDate());
                // projectDate.setDate(projectDate.getDate() - 1);

                newInstallments.push({
                    projectedAmount: installmentAmount,
                    receivedAmount: null,
                    projectedDate: projectedDate.toISOString().split('T')[0],
                    receivedDate: null,
                    type: frequency
                });
            }
            setPreviewInstallments(newInstallments);

        } catch (error) {
            console.error("Error generating installments:", error);
            toast.error("Failed to generate installments.", { position: "top-right" });
        } finally {
            setIsLoadingOverlayVisible(false);
        }
    };

    useEffect(() => {
        if (subscriptionModalOpen) {
            generateInstallments(
                editedSubscriptionData.subscriptionTypeId,
                editedSubscriptionData.price,
                editedSubscriptionData.startDate,
                editedSubscriptionData.endDate,
                editedSubscriptionData.paymentType,
            );
        }
    }, [editedSubscriptionData.subscriptionTypeId, editedSubscriptionData.price, editedSubscriptionData.startDate, editedSubscriptionData.endDate, editedSubscriptionData.paymentType, subscriptionModalOpen]);

    const handleSubscriptionInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "subscriptionTypeId" && value !== "") {
            const subscriptionTypeId = Number(value);
            setEditedSubscriptionData(prev => ({ ...prev, [name]: subscriptionTypeId }));

        } else if (name === "price") {
            const price = Number(value);
            setEditedSubscriptionData(prev => ({ ...prev, [name]: price }));

        }
        else {
            setEditedSubscriptionData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSaveSubscription = async () => {
        setIsLoadingOverlayVisible(true);
        try {
            const selectedType = STATIC_SUBSCRIPTION_TYPES.find(t => t.subscriptionTypeId === editedSubscriptionData.subscriptionTypeId);

            const dataToSend = {
                customerId: customerId,
                subscriptionTypeId: selectedType?.subscriptionTypeId,
                startDate: editedSubscriptionData.startDate,
                endDate: editedSubscriptionData.endDate,
                price: Number(editedSubscriptionData.price) || 0,
                currencyId: 1,
                paymentType: editedSubscriptionData.paymentType,
                paymentDetails: editedSubscriptionData.paymentDetails,
                // Inventory-based billing: one-time upfront + per active-unit cost charged each period.
                upfrontAmount:
                    editedSubscriptionData.upfrontAmount != null && editedSubscriptionData.upfrontAmount !== ''
                        ? Number(editedSubscriptionData.upfrontAmount)
                        : null,
                perUnitCost:
                    editedSubscriptionData.perUnitCost != null && editedSubscriptionData.perUnitCost !== ''
                        ? Number(editedSubscriptionData.perUnitCost)
                        : null,
            };

            let response;
            if (subscription?.subscriptionId) {
                response = await axiosInstance.put(`/subscriptions?subscription_id=${subscription.subscriptionId}`, dataToSend);
                toast.success("Subscription details updated successfully!", { position: "top-right" });
            } else {

                response = await axiosInstance.post(`/subscriptions`, dataToSend);
                toast.success("Subscription details created successfully!", { position: "top-right" });

            }

            const subscriptionData = response;
            setSubscription(subscriptionData);
            setSubscriptionModalOpen(false);
            setPreviewInstallments([]);

            // The backend creates the upfront installment on subscription creation; periodic invoices
            // are generated by the billing cron based on active inventory. Just refetch what exists.
            try {
                if (subscriptionData?.subscriptionId) {
                    const updatedInstallments = await axiosInstance.get(
                        `/subscriptions/customer/installment?subscriptionId=${subscriptionData.subscriptionId}`
                    );
                    const installmentsArray =
                        updatedInstallments?.items || (Array.isArray(updatedInstallments) ? updatedInstallments : []);
                    setInstallments(installmentsArray);
                }
            } catch (installmentError) {
                console.error("Error fetching installments:", installmentError);
            }

            setEditingSubscription(false);
            setActiveTab("installments");

        } catch (err) {
            setError(err.message);
            console.error("Error updating subscription details:", err);
            toast.error(`Error updating subscription: ${err.response?.data?.message || err.message}`, { position: "top-right" });
        } finally {
            setIsLoadingOverlayVisible(false);
        }
    };

    const handleCancelSubscriptionEdit = () => {
        setEditedSubscriptionData(subscription || {});
        setSubscriptionModalOpen(false);
        setPreviewInstallments([]); // Clear preview when canceling
    };

    const addInstallmentRow = () => {
        setNewInstallment({
            projectedAmount: '',
            receivedAmount: '',
            projectedDate: '',
            receivedDate: '',
        });
        setEditingRowIndex(previewInstallments.length);
    };

    const cancelAddRow = () => {
        setNewInstallment(null);
        setEditingRowIndex(null);
    };

    const handleInstallmentChange = (index, field, value) => {
        if (newInstallment && index === previewInstallments.length) {
            setNewInstallment(prev => ({ ...prev, [field]: value }));
        } else if (index < previewInstallments.length) {
            const updatedInstallments = [...previewInstallments];
            updatedInstallments[index] = { ...updatedInstallments[index], [field]: value };
            setPreviewInstallments(updatedInstallments);
        }
    };

    const isTrialExpired = () => {
        if (!subscription?.endDate) return false;
        const endDate = new Date(subscription.endDate);
        const now = new Date();
        const isTrial = subscription?.subscriptionType?.typeName?.toLowerCase().includes('trial');
        return isTrial && endDate < now;
    };

    const displayEndDate = subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : '-';
    const trialStatusStyle = isTrialExpired() ? 'font-normal text-red-500' : '';

    const isEnterpriseSubscription = () => {
        return subscription?.subscriptionType?.typeName === 'ENTERPRISE';
    };

    const isStandardSubscription = () => {
        return subscription?.subscriptionType?.typeName === 'STANDARD';
    };

    const isTrialOrReadOnlySubscription = () => {
        return subscription?.subscriptionType?.typeName === 'TRIAL' || subscription?.subscriptionType?.typeName === 'TRIAL_READ_ONLY';
    }

    const handleSaveNewInstallment = () => {
        // Assuming the installments are being previewed.
        if (!newInstallment) return;

        const updatedPreviewInstallments = [...previewInstallments, newInstallment];
        setPreviewInstallments(updatedPreviewInstallments);
        setNewInstallment(null);
        setEditingRowIndex(null);
    };

    const handleUpdateInstallment = (index) => {
        // Update logic based on preview installments
        if (editingRowIndex === null || editingRowIndex >= previewInstallments.length) return;

        const updatedPreviewInstallments = [...previewInstallments];
        updatedPreviewInstallments[editingRowIndex] = { ...previewInstallments[editingRowIndex] };
        setPreviewInstallments(updatedPreviewInstallments);
        setEditingRowIndex(null);
    };

    // --- Real (persisted) installment handlers for the Installments tab ---
    const refetchInstallments = async () => {
        if (!subscription?.subscriptionId) return;
        const res = await axiosInstance.get(
            `/subscriptions/customer/installment?subscriptionId=${subscription.subscriptionId}`
        );
        setInstallments(res?.items || (Array.isArray(res) ? res : []));
    };

    const handleRealInstallmentChange = (index, field, value) => {
        if (newInstallment && index === installments.length) {
            setNewInstallment({ ...newInstallment, [field]: value });
        } else if (index < installments.length) {
            const updated = [...installments];
            updated[index] = { ...updated[index], [field]: value };
            setInstallments(updated);
        }
    };

    const handleSaveRealInstallment = async () => {
        if (editingRowIndex === null || editingRowIndex >= installments.length) return;
        const inst = installments[editingRowIndex];
        setIsLoadingOverlayVisible(true);
        try {
            await axiosInstance.put(
                `/subscriptions/customer/installment?subscription_customer_installment_id=${inst.subscriptionCustomerInstallmentId}`,
                {
                    subscriptionId: inst.subscriptionId ?? subscription?.subscriptionId,
                    projectedAmount: Number(inst.projectedAmount),
                    receivedAmount:
                        inst.receivedAmount != null && inst.receivedAmount !== '' ? Number(inst.receivedAmount) : null,
                    projectedDate: inst.projectedDate || null,
                    receivedDate: inst.receivedDate || null,
                }
            );
            toast.success("Installment updated successfully!", { position: "top-right" });
            await refetchInstallments();
            setEditingRowIndex(null);
        } catch (err) {
            toast.error(
                `Error updating installment: ${err.response?.data?.detail || err.response?.data?.message || err.message}`,
                { position: "top-right" }
            );
        } finally {
            setIsLoadingOverlayVisible(false);
        }
    };

    const handleGenerateReceipt = async (installment) => {
        const id = installment?.subscriptionCustomerInstallmentId;
        if (!id) return;
        // Once a receipt exists, don't regenerate — download the existing one instead.
        if (installment.receiptUrl) {
            return handleDownloadReceipt(installment);
        }
        if (
            installment.receivedAmount == null ||
            installment.receivedAmount === '' ||
            !installment.receivedDate
        ) {
            toast.error("Set received amount and date before generating a receipt", { position: "top-right" });
            return;
        }
        setIsLoadingOverlayVisible(true);
        try {
            await axiosInstance.post(`/subscriptions/installment/${id}/receipt`);
            toast.success("Receipt generated and emailed to the developer", { position: "top-right" });
            await refetchInstallments();
        } catch (err) {
            toast.error(`Error generating receipt: ${err.response?.data?.detail || err.message}`, {
                position: "top-right",
            });
        } finally {
            setIsLoadingOverlayVisible(false);
        }
    };

    const handleDownloadReceipt = async (installment) => {
        const id = installment?.subscriptionCustomerInstallmentId;
        if (!id) return;
        try {
            const res = await axiosInstance.get(`/subscriptions/installment/${id}/receipt/download`);
            const url = res?.url || res?.data?.url;
            if (url) {
                window.open(url, "_blank", "noopener");
            } else {
                toast.error("Receipt URL unavailable", { position: "top-right" });
            }
        } catch (err) {
            toast.error(`Could not open receipt: ${err.response?.data?.detail || err.message}`, {
                position: "top-right",
            });
        }
    };

    const handleGenerateInvoice = async (installment) => {
        const id = installment?.subscriptionCustomerInstallmentId;
        if (!id) return;
        // Once an invoice exists, don't regenerate — download the existing one instead.
        if (installment.invoiceUrl) {
            return handleDownloadInvoice(installment);
        }
        setIsLoadingOverlayVisible(true);
        try {
            await axiosInstance.post(`/subscriptions/installment/${id}/invoice`);
            toast.success("Invoice generated and emailed to the developer", { position: "top-right" });
            await refetchInstallments();
        } catch (err) {
            toast.error(`Error generating invoice: ${err.response?.data?.detail || err.message}`, {
                position: "top-right",
            });
        } finally {
            setIsLoadingOverlayVisible(false);
        }
    };

    const handleDownloadInvoice = async (installment) => {
        const id = installment?.subscriptionCustomerInstallmentId;
        if (!id) return;
        try {
            const res = await axiosInstance.get(`/subscriptions/installment/${id}/invoice/download`);
            const url = res?.url || res?.data?.url;
            if (url) {
                window.open(url, "_blank", "noopener");
            } else {
                toast.error("Invoice URL unavailable", { position: "top-right" });
            }
        } catch (err) {
            toast.error(`Could not open invoice: ${err.response?.data?.detail || err.message}`, {
                position: "top-right",
            });
        }
    };

    const getSubscriptionTag = (typeName) => (
        <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${{
            BASIC: "bg-blue-100 text-blue-700",
            STANDARD: "bg-green-100 text-green-700",
            TRIAL_READ_ONLY: "bg-yellow-100 text-yellow-700",
            ENTERPRISE: "bg-purple-100 text-purple-700",
            TRIAL: "bg-red-100 text-red-700",
        }[typeName] || "bg-gray-100 text-gray-700"
            }`}>
            {typeName || 'N/A'}
        </div>
    );
    const handleEditClick = (index) => {
        setEditingRowIndex(index);
    };

    const handleCancelEdit = () => {
        setEditingRowIndex(null);
    };

    const handleDeleteClick = (installmentId, index) => {
        if (subscriptionModalOpen) {

            const updatedPreviewInstallments = [...previewInstallments];
            updatedPreviewInstallments.splice(index, 1);
            setPreviewInstallments(updatedPreviewInstallments);

        } else {
            if (!installmentId) {
                console.error("Cannot delete: Installment ID is missing.");
                return;
            }
            setInstallmentToDelete({ installmentId, index });
            setConfirmationModalOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        setConfirmationModalOpen(false);
        if (!installmentToDelete) return;

        const { installmentId, index } = installmentToDelete;
        setIsLoadingOverlayVisible(true);
        try {
            await axiosInstance.delete(`/subscriptions/customer/installment?subscription_installment_id=${installmentId}`);
            const updatedInstallments = installments.filter((_, i) => i !== index);
            setInstallments(updatedInstallments);
            toast.success("Installment deleted successfully!", { position: "top-right" });
        } catch (err) {
            setError(err.message);
            console.error("Error deleting installment:", err);
            toast.error(`Error deleting installment: ${err.response?.data?.message || err.message}`, { position: "top-right" });
        } finally {
            setIsLoadingOverlayVisible(false);
            setInstallmentToDelete(null);
        }
    };
    const toggleEmailPasswordVisibility = () => {
        setEmailPasswordVisible(!emailPasswordVisible);
    };

    const toggleWhatsappTokenVisibility = () => {
        setWhatsappTokenVisible(!whatsappTokenVisible);
    };
    const handleCancelDelete = () => {
        setConfirmationModalOpen(false);
        setInstallmentToDelete(null);
    };

    const formatAddress = (address) => {
        if (!address) return 'N/A';
        const parts = [
            address.buildingNo,
            address.streetName1,
            address.streetName2,
            address.city,
            address.district,
            address.state,
            address.country,
            address.pinCode,
        ];
        return parts.filter(part => part != null && part !== '').join(', ') || 'N/A';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString();
        } catch (e) {
            console.error("Error formatting date:", dateString, e);
            return 'Invalid Date';
        }
    };

    const getInputValueDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return '';
            }
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    const handleGenerateInstallments = () => {
        // Assuming editedSubscriptionData.subscriptionTypeId and editedSubscriptionData.price are already set
        generateInstallments(editedSubscriptionData.subscriptionTypeId, editedSubscriptionData.price);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <ThreeDots height="80" width="80" color="#4fa94d" ariaLabel="loading" />
            </div>
        );
    }

    if (error && !customer && !subscription) {
        return <div className="p-6 text-red-500">Error loading customer data: {error}</div>;
    }
    const data = [
        {
            label: "Customer & Subscription",
            value: "customerSubscription",
            content: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Customer card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="flex justify-between items-center px-5 py-4 border-b border-slate-100">
                            <h2 className="text-base font-semibold text-slate-900">Customer Details</h2>
                            {editingCustomer ? (
                                <div className="flex gap-2">
                                    <PrimaryButton size="sm" onClick={handleSaveCustomer} disabled={isLoadingOverlayVisible}>
                                        {isLoadingOverlayVisible ? <ThreeDots height="18" width="18" color="#fff" /> : 'Save'}
                                    </PrimaryButton>
                                    <OutlineButton size="sm" onClick={handleCancelCustomerEdit} disabled={isLoadingOverlayVisible}>Cancel</OutlineButton>
                                </div>
                            ) : (
                                <EditButton onClick={handleEditCustomer}>Edit</EditButton>
                            )}
                        </div>
                        <div className="p-5">
                            {editingCustomer ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div><label className="text-xs text-slate-500">Company Name</label><CustomInput type="text" name="companyName" value={editedCustomerData.companyName || ''} onChange={handleCustomerInputChange} /></div>
                                    <div><label className="text-xs text-slate-500">Owner Name</label><CustomInput type="text" name="ownerName" value={editedCustomerData.ownerName || ''} onChange={handleCustomerInputChange} /></div>
                                    <div><label className="text-xs text-slate-500">Email</label><CustomInput type="email" name="emailId" value={editedCustomerData.emailId || ''} onChange={handleCustomerInputChange} /></div>
                                    <div><label className="text-xs text-slate-500">Mobile (WhatsApp)</label><CustomInput type="tel" name="mobileNoWa" value={editedCustomerData.mobileNoWa || ''} onChange={handleCustomerInputChange} /></div>
                                    <div><label className="text-xs text-slate-500">Mobile (Calling)</label><CustomInput type="tel" name="mobileNoCalling" value={editedCustomerData.mobileNoCalling || ''} onChange={handleCustomerInputChange} /></div>
                                    <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                                        <input
                                            type="checkbox"
                                            id="idDeveloperApprovalRequired"
                                            className="h-4 w-4 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                                            checked={!!editedCustomerData.idDeveloperApprovalRequired}
                                            onChange={(e) => setEditedCustomerData(prev => ({ ...prev, idDeveloperApprovalRequired: e.target.checked }))}
                                        />
                                        <label htmlFor="idDeveloperApprovalRequired" className="text-xs text-slate-600">
                                            Interior Design Developer Approval Required
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Company</dt><dd className="text-slate-900 font-medium mt-0.5">{customer?.companyName || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Owner</dt><dd className="text-slate-900 font-medium mt-0.5">{customer?.ownerName || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Email</dt><dd className="text-slate-900 mt-0.5 break-all">{customer?.emailId || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Mobile (WhatsApp)</dt><dd className="text-slate-900 mt-0.5">{customer?.mobileNoWa || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Mobile (Calling)</dt><dd className="text-slate-900 mt-0.5">{customer?.mobileNoCalling || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">Type</dt><dd className="text-slate-900 mt-0.5">{customer?.customerType || '—'}</dd></div>
                                    <div><dt className="text-xs uppercase tracking-wide text-slate-400">ID Developer Approval</dt><dd className="text-slate-900 mt-0.5">{customer?.idDeveloperApprovalRequired ? 'Required' : 'Not required'}</dd></div>
                                    <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-slate-400">Address</dt><dd className="text-slate-900 mt-0.5">{formatAddress(customer?.address)}</dd></div>
                                </dl>
                            )}
                        </div>
                    </div>

                    {/* Subscription card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="flex justify-between items-center px-5 py-4 border-b border-slate-100">
                            <h2 className="text-base font-semibold text-slate-900">Subscription &amp; Payment Plan</h2>
                            <EditButton onClick={handleEditSubscription}>{subscription ? 'Edit' : 'Add'}</EditButton>
                        </div>
                        <div className="p-5">
                            {subscription ? (
                                <>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-flamingo-50 text-flamingo-700 border border-flamingo-200">
                                            {subscription?.subscriptionType?.typeName || 'No plan'}
                                        </span>
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                            {subscription?.paymentType || '—'}
                                        </span>
                                    </div>
                                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                        <div><dt className="text-xs uppercase tracking-wide text-slate-400">Upfront Amount</dt><dd className="text-slate-900 font-medium mt-0.5 tabular-nums">{subscription?.upfrontAmount != null ? `₹${subscription.upfrontAmount}` : '—'}</dd></div>
                                        <div><dt className="text-xs uppercase tracking-wide text-slate-400">Per Inventory Cost</dt><dd className="text-slate-900 font-medium mt-0.5 tabular-nums">{subscription?.perUnitCost != null ? `₹${subscription.perUnitCost}` : '—'}</dd></div>
                                        <div><dt className="text-xs uppercase tracking-wide text-slate-400">Start Date</dt><dd className="text-slate-900 mt-0.5">{formatDate(subscription?.startDate)}</dd></div>
                                        <div><dt className="text-xs uppercase tracking-wide text-slate-400">End Date</dt><dd className="text-slate-900 mt-0.5">{subscription?.endDate ? formatDate(subscription.endDate) : '—'}</dd></div>
                                        {subscription?.paymentDetails && (
                                            <div className="col-span-2"><dt className="text-xs uppercase tracking-wide text-slate-400">Notes</dt><dd className="text-slate-700 mt-0.5">{subscription.paymentDetails}</dd></div>
                                        )}
                                    </dl>
                                    <p className="text-xs text-slate-400 mt-4">
                                        Each {(subscription?.paymentType || 'period').toLowerCase()} an invoice = active inventory × per-inventory cost is generated automatically. See the Installments tab.
                                    </p>
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center text-center py-10">
                                    <p className="text-sm font-medium text-slate-700">No subscription yet</p>
                                    <p className="text-xs text-slate-500 mt-1 mb-4">Add a plan and payment plan to start billing this developer.</p>
                                    <button
                                        type="button"
                                        onClick={handleEditSubscription}
                                        className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                                    >
                                        Add Subscription
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            label: "Installments",
            value: "installments",
            content: (
                <div>

                    <InstallmentsTable
                        installments={installments}
                        subscription={subscription}
                        editingRowIndex={editingRowIndex}
                        newInstallment={newInstallment}
                        handleInstallmentChange={handleRealInstallmentChange}
                        getInputValueDate={getInputValueDate}
                        formatDate={formatDate}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        handleUpdateInstallment={handleSaveRealInstallment}
                        handleGenerateReceipt={handleGenerateReceipt}
                        handleDownloadReceipt={handleDownloadReceipt}
                        handleGenerateInvoice={handleGenerateInvoice}
                        handleDownloadInvoice={handleDownloadInvoice}
                        cancelAddRow={cancelAddRow}
                        handleSaveNewInstallment={handleSaveNewInstallment}
                        handleCancelEdit={handleCancelEdit}
                        isLoadingOverlayVisible={isLoadingOverlayVisible}
                        addInstallmentRow={addInstallmentRow}
                    />

                </div>
            ),
        },
    ];

    return (
        <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
            <LoadingOverlay isVisible={isLoadingOverlayVisible} />

            {/* Back to list */}
            <button
                type="button"
                onClick={() => navigate('/zumale_user-dashboard')}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-flamingo-600 mb-4 transition-colors"
            >
                <ArrowLeft size={16} /> Back to Developers
            </button>

            {/* Hero header */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center shrink-0">
                            <Building2 size={24} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-xl font-bold text-slate-900 truncate">
                                {customer?.companyName || 'Developer'}
                            </h1>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1 flex-wrap">
                                {customer?.ownerName && <span className="inline-flex items-center gap-1"><BadgeCheck size={13} /> {customer.ownerName}</span>}
                                {customer?.emailId && <span className="inline-flex items-center gap-1"><Mail size={13} /> {customer.emailId}</span>}
                                {customer?.mobileNoWa && <span className="inline-flex items-center gap-1"><Phone size={13} /> {customer.mobileNoWa}</span>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {subscription?.subscriptionType?.typeName && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-flamingo-50 text-flamingo-700 border border-flamingo-200">
                                {subscription.subscriptionType.typeName}
                            </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${customer?.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${customer?.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                            {customer?.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Tab bar */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex border-b border-slate-200">
                    {data.map(({ label, value }) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setActiveTab(value)}
                            className={`flex-1 py-3 px-6 text-sm cursor-pointer text-center transition-colors ${activeTab === value
                                ? "text-flamingo-700 border-b-2 border-flamingo-600 font-semibold bg-flamingo-50/40"
                                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="p-5">
                    {data.find(({ value }) => value === activeTab)?.content}
                </div>
            </div>

            {/* --- Confirmation Modal --- */}
            <ConfirmationModal
                isOpen={confirmationModalOpen}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message="Are you sure you want to delete this installment?"
            />

            {/* Subscription Modal */}

            <div
                className={`fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 ${subscriptionModalOpen ? '' : 'hidden'}`}
            >
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                    <h2 className="text-lg font-semibold mb-4">Edit Subscription Details</h2>
                    <div className="grid grid-cols-1 gap-4">
                        {/* Subscription Details */}
                        <div className="p-4 ">
                            <h3 className="text-lg font-semibold mb-2">Subscription Details</h3>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Subscription Type</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomSelect
                                            name="subscriptionTypeId"
                                            value={editedSubscriptionData.subscriptionTypeId}
                                            onChange={handleSubscriptionInputChange}
                                            options={subscriptionTypeOptions}
                                            placeholder="Select Subscription Type"
                                            className="mt-1"
                                        />
                                    </dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Payment Type</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomSelect
                                            name="paymentType"
                                            value={editedSubscriptionData.paymentType || ""}
                                            onChange={handleSubscriptionInputChange}
                                            options={paymentTypeOptions}
                                            placeholder="Select Payment Type"
                                            className="mt-1"
                                        />
                                    </dd>
                                </div>

                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Payment Details</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomInput
                                            type="text"
                                            name="paymentDetails"
                                            value={editedSubscriptionData.paymentDetails || ""}
                                            onChange={handleSubscriptionInputChange}
                                        />
                                    </dd>
                                </div>

                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Upfront Amount (one-time)</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomInput
                                            type="number"
                                            step="0.01"
                                            name="upfrontAmount"
                                            value={editedSubscriptionData.upfrontAmount ?? ""}
                                            onChange={handleSubscriptionInputChange}
                                            placeholder="Charged once at activation"
                                        />
                                    </dd>
                                </div>

                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Per Inventory Cost</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomInput
                                            type="number"
                                            step="0.01"
                                            name="perUnitCost"
                                            value={editedSubscriptionData.perUnitCost ?? ""}
                                            onChange={handleSubscriptionInputChange}
                                            placeholder="Per active unit, billed each period"
                                        />
                                    </dd>
                                </div>

                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomInput
                                            type="date"
                                            name="startDate"
                                            value={getInputValueDate(editedSubscriptionData.startDate)}
                                            onChange={handleSubscriptionInputChange}
                                        />
                                    </dd>
                                </div>

                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">End Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <CustomInput
                                            type="date"
                                            name="endDate"
                                            value={getInputValueDate(editedSubscriptionData.endDate)}
                                            onChange={handleSubscriptionInputChange}
                                        />
                                    </dd>
                                </div>

                            </dl>
                        </div>

                        {/* Billing note — installments are system-generated, not authored here. */}
                        <div className=" p-4 ">
                            <h3 className="text-lg font-semibold mb-2">Billing</h3>
                            <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm p-4">
                                On save, an <strong>upfront installment</strong> is created automatically. Thereafter,
                                at the end of every {(editedSubscriptionData.paymentType || "MONTHLY").toLowerCase()}{" "}
                                period a signed invoice is generated based on the developer's active inventory
                                (units in active phases) × the per-inventory cost, with a 15-day payment grace.
                                Manage these under the <strong>Installments</strong> tab.
                            </div>
                            <div className="flex justify-end mt-4">
                                <OutlineButton
                                    onClick={() => {
                                        setSubscriptionModalOpen(false);
                                        handleCancelSubscriptionEdit();
                                    }}
                                >
                                    Cancel
                                </OutlineButton>
                                <PrimaryButton
                                    onClick={() => {
                                        handleSaveSubscription();
                                    }}
                                    className="ml-2"
                                >
                                    Save Subscription
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

function TabPanel({ children, value }) {
    return (
        <div hidden={value !== value}>
            {children}
        </div>
    );
}

export default ViewCustomer;